import { Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FormlyModule } from '@ngx-formly/core';

import { FormlyFieldBaseComponent } from '../formly-field-base.component';
import { MenuService } from '../../../services';
import { FormlyProps } from '../../../models';

@Component({
  selector: 'app-formly-select',
  templateUrl: './formly-select.component.html',
  styleUrl: './formly-select.component.scss',
  imports: [NgSelectComponent, FormsModule, ReactiveFormsModule, FormlyModule, AsyncPipe],
})
export class FormlySelectComponent extends FormlyFieldBaseComponent {
  protected readonly menuService = inject(MenuService);

  defaultOptions = {
    props: { options: [] },
  };

  // Where a `floatDropdown` select renders its panel. Inside a modal it is the MODAL WINDOW,
  // not the body: the window (`.modal`, position fixed, overflow-y auto) is outside
  // `.modal-content`'s overflow: hidden so nothing clips the panel, and the panel stays inside
  // the modal's own stacking context, focus trap and click handling. Appended to the body the
  // panel sat UNDER the transparent full-screen modal window (ng-select's 1050 vs Bootstrap's
  // 1055), so a click on an option landed on the modal instead - the modal bumped and the
  // select closed with nothing chosen (Website Connection modal, 2026-09-04). The last window
  // is the topmost one when modals are stacked. Body only when no modal is open.
  get appendTo(): string | null {
    if (!this.props.floatDropdown) {
      return null;
    }
    return document.querySelector('ngb-modal-window') ? 'ngb-modal-window:last-of-type' : 'body';
  }

  get showCreate(): boolean {
    return this.props.create && (!this.props.showCreate || this.props.showCreate(this.model));
  }

  get showUpdate(): boolean {
    return this.props.update && (!this.props.showUpdate || this.props.showUpdate(this.model));
  }

  get showDelete(): boolean {
    return this.props.delete && (!this.props.showDelete || this.props.showDelete(this.model));
  }

  getValue(options: FormlyProps[]) {
    const value = this.model[this.key as string];

    // A multi-select holds an ARRAY, so read-only mode has to list every label rather
    // than look one up - otherwise the field just shows blank.
    if (Array.isArray(value)) {
      return value
        .map((x: any) => options.find((option: FormlyProps) => option.value === x)?.label)
        .filter((x: string) => !!x)
        .join(', ');
    }

    return value ? options.find((option: FormlyProps) => option.value === value)?.label : '';
  }

  // Honours props.onChange the way the checkbox does, so a screen can react to a pick -
  // e.g. the Quality screen filling its dent-pattern fields from the chosen defaults.
  // Fires after ng-select has written the value to the form control; event is the selected
  // option ({ value, label }), or undefined when cleared.
  onChange(event: any) {
    if (this.props.onChange) {
      this.props.onChange(event);
    }
  }

  onCreate(event: any) {
    if (this.props.create) {
      this.props.create(event);
    }
  }

  onUpdate(event: any) {
    if (this.props.update) {
      this.props.update(event);
    }
  }

  onDelete(event: any) {
    if (this.props.delete) {
      this.props.delete(event);
    }
  }
}
